import {
  JOB_PRIORITY_LABELS,
  JOB_STATUS_LABELS,
  SERVICE_TYPE_LABELS,
  type Job,
  type JobPriority,
} from '@uroute/shared';
import { JobStatusPill } from '@uroute/ui';
import { addressLine } from '../lib/format';
import { DeadlineTag } from './DeadlineTag';
import { ChevronRight, Package, PenIcon, Weight } from './icons';

const PRIORITY_CLASS: Record<JobPriority, string> = {
  urgent: 'chip--urgent',
  high: 'chip--high',
  normal: 'chip--normal',
  low: 'chip--low',
};

export interface JobCardProps {
  job: Job;
  onClick: () => void;
}

/** Primary job surface: route, urgency, load and status in one tap target. */
export function JobCard({ job, onClick }: JobCardProps) {
  const instructions = job.handlingInstructions?.trim();

  return (
    <button
      type="button"
      className="jobcard"
      onClick={onClick}
      data-testid="job-card"
      data-job-id={job.id}
      data-status={job.status}
      data-priority={job.priority}
    >
      <span className="jobcard__top">
        <span className="jobcard__ref u-data">{job.externalRef}</span>
        <span className="jobcard__badges">
          <span className={`chip ${PRIORITY_CLASS[job.priority]}`}>{JOB_PRIORITY_LABELS[job.priority]}</span>
          <JobStatusPill status={job.status} label={JOB_STATUS_LABELS[job.status]} />
        </span>
      </span>

      <span className="jobcard__route">
        <span className="jobcard__stop">
          <span className="jobcard__node jobcard__node--from" aria-hidden="true" />
          <span className="jobcard__stopbody">
            <span className="jobcard__stoplabel">Collection</span>
            <span className="jobcard__stopaddr">
              {addressLine(job.collectionAddress.line1, job.collectionAddress.city, job.collectionAddress.postcode)}
            </span>
          </span>
        </span>
        <span className="jobcard__spine" aria-hidden="true" />
        <span className="jobcard__stop">
          <span className="jobcard__node jobcard__node--to" aria-hidden="true" />
          <span className="jobcard__stopbody">
            <span className="jobcard__stoplabel">Delivery</span>
            <span className="jobcard__stopaddr">
              {addressLine(job.deliveryAddress.line1, job.deliveryAddress.city, job.deliveryAddress.postcode)}
            </span>
          </span>
        </span>
      </span>

      <span className="jobcard__footer">
        <DeadlineTag job={job} />
        <span className="jobcard__meta">
          <span className="jobcard__metaitem">
            <Weight size={14} />
            <span className="u-data">{job.weightKg} kg</span>
          </span>
          <span className="jobcard__metaitem">
            <Package size={14} />
            <span className="u-data">{job.parcels}</span>&nbsp;parcel{job.parcels === 1 ? '' : 's'}
          </span>
          <span className="jobcard__metaitem">{SERVICE_TYPE_LABELS[job.serviceType]}</span>
        </span>
      </span>

      {instructions ? (
        <span className="jobcard__hint">
          <PenIcon size={14} />
          <span className="jobcard__hinttext">{instructions}</span>
        </span>
      ) : null}

      <ChevronRight className="jobcard__chevron" size={18} />
    </button>
  );
}
